import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, Megaphone } from '@/lib/icons'
import type { BroadcastAudience, BroadcastCreateRequest, BroadcastListItem } from '@ayub/shared'
import { BLOGGER_STAGE_LABELS, BloggerStage, formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import type { AudienceKind } from './AudienceSection'
import { AudienceSection } from './AudienceSection'
import { createBroadcast } from './api'
import { BroadcastHistory } from './BroadcastHistory'
import { invalidateBroadcastCaches, useBroadcastPreview } from './hooks'
import { ManualBloggerPickerModal } from './ManualBloggerPickerModal'
import { MessageComposer } from './MessageComposer'
import { PreviewDialog } from './PreviewDialog'
import { ProgressDialog } from './ProgressDialog'

// Telegram Bot API hujjati: `sendMessage` matni 4096 belgigacha, media caption'i
// (`sendPhoto`/`sendVideo`) esa 1024 belgigacha. Serverda bir xil son bor
// (`apps/server/src/services/broadcast.ts`: `TELEGRAM_MESSAGE_TEXT_LIMIT`/
// `TELEGRAM_CAPTION_LIMIT`) — u yerdan import qilib bo'lmaydi (alohida ilova, umumiy
// joy `@ayub/shared`da ham yo'q), shuning uchun MIJOZ tomonda QASDAN takrorlanadi: admin
// "Yuborish"ni bosishidan OLDIN xatoni ko'rishi kerak, server 422'sini kutmasdan.
const TELEGRAM_MESSAGE_TEXT_LIMIT = 4096
const TELEGRAM_CAPTION_LIMIT = 1024

function describeAudience(kind: AudienceKind, stage: BloggerStage, manualCount: number): string {
  if (kind === 'stage') return `Bosqich: ${BLOGGER_STAGE_LABELS[stage]}`
  if (kind === 'manual') return `${formatNumber(manualCount)} ta qo'lda tanlangan bloger`
  return 'Barcha blogerlar'
}

/**
 * Sahifa 5 — Broadcast (`docs/03-app-flow.md`). Butun compose holati (auditoriya, matn,
 * media) shu yerda saqlanadi — pastdagi bo'lim komponentlari (`AudienceSection`,
 * `MessageComposer`) "situ" — faqat props orqali boshqariladi, o'z holatini saqlamaydi.
 * Bu `PayoutsPage.tsx` naqshiga mos: bitta sahifa komponenti hamma tab/bo'lim holatini
 * ushlab turadi, pastdagilar shunchaki ko'rinish.
 */
export function BroadcastPage() {
  const queryClient = useQueryClient()

  const [audienceKind, setAudienceKind] = useState<AudienceKind>('all')
  const [stage, setStage] = useState<BloggerStage>(BloggerStage.ACTIVE)
  const [manualIds, setManualIds] = useState<Set<number>>(new Set())
  const [manualNames, setManualNames] = useState<Map<number, string>>(new Map())
  const [pickerOpen, setPickerOpen] = useState(false)

  const [textUz, setTextUz] = useState('')
  const [textRu, setTextRu] = useState('')
  const [fileId, setFileId] = useState('')
  const [fileType, setFileType] = useState<'photo' | 'video'>('photo')

  const [previewOpen, setPreviewOpen] = useState(false)
  const [activeBroadcastId, setActiveBroadcastId] = useState<number | null>(null)
  const [historyPage, setHistoryPage] = useState(1)

  const audience: BroadcastAudience = useMemo(() => {
    if (audienceKind === 'stage') return { kind: 'stage', stage }
    if (audienceKind === 'manual') return { kind: 'manual', bloggerIds: Array.from(manualIds) }
    return { kind: 'all' }
  }, [audienceKind, stage, manualIds])

  const {
    preview,
    isLoading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useBroadcastPreview(audience)

  const hasMedia = fileId.trim().length > 0
  const charLimit = hasMedia ? TELEGRAM_CAPTION_LIMIT : TELEGRAM_MESSAGE_TEXT_LIMIT

  const trimmedUz = textUz.trim()
  const trimmedRu = textRu.trim()
  const textValid =
    trimmedUz.length > 0 &&
    trimmedRu.length > 0 &&
    trimmedUz.length <= charLimit &&
    trimmedRu.length <= charLimit
  const audienceValid = audienceKind !== 'manual' || manualIds.size > 0
  const canOpenPreview = textValid && audienceValid

  const createMutation = useMutation<BroadcastListItem, Error, BroadcastCreateRequest>({
    mutationFn: createBroadcast,
    onSuccess: (item) => {
      invalidateBroadcastCaches(queryClient)
      setPreviewOpen(false)
      setActiveBroadcastId(item.id)

      // Yuborish DARHOL (fon rejimida) boshlangan — server tomon endi mustaqil ishlaydi
      // (`services/broadcast.ts`: deliverable ro'yxati SNAPSHOT qilingan). Shuning uchun
      // formani darhol tozalash xavfsiz — admin keyingi broadcastni yozishni davom
      // ettiraveradi, progress esa alohida oynada (`ProgressDialog`) kuzatiladi.
      setTextUz('')
      setTextRu('')
      setFileId('')
      setFileType('photo')
      setAudienceKind('all')
      setStage(BloggerStage.ACTIVE)
      setManualIds(new Set())
      setManualNames(new Map())
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Megaphone size={20} aria-hidden="true" /> Ommaviy xabar
        </h1>
      </header>

      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        <AudienceSection
          kind={audienceKind}
          onKindChange={setAudienceKind}
          stage={stage}
          onStageChange={setStage}
          manualCount={manualIds.size}
          onOpenManualPicker={() => setPickerOpen(true)}
          preview={preview}
          previewLoading={previewLoading}
          previewError={previewError}
          onRetryPreview={() => void refetchPreview()}
        />

        <MessageComposer
          textUz={textUz}
          onTextUzChange={setTextUz}
          textRu={textRu}
          onTextRuChange={setTextRu}
          fileId={fileId}
          onFileIdChange={setFileId}
          fileType={fileType}
          onFileTypeChange={setFileType}
          charLimit={charLimit}
        />

        <div className="flex justify-end">
          <Button onClick={() => setPreviewOpen(true)} disabled={!canOpenPreview}>
            <Eye size={16} aria-hidden="true" />
            Ko&apos;rib chiqish
          </Button>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-3">
        <h2 className="text-sm font-semibold text-app-text">Tarix</h2>
        <BroadcastHistory
          page={historyPage}
          onPageChange={setHistoryPage}
          onWatchProgress={setActiveBroadcastId}
        />
      </div>

      <ManualBloggerPickerModal
        open={pickerOpen}
        initialSelectedIds={manualIds}
        initialSelectedNames={manualNames}
        onConfirm={(ids, names) => {
          setManualIds(ids)
          setManualNames(names)
          setPickerOpen(false)
        }}
        onClose={() => setPickerOpen(false)}
      />

      <PreviewDialog
        open={previewOpen}
        audienceSummary={describeAudience(audienceKind, stage, manualIds.size)}
        deliverableCount={preview?.deliverable ?? 0}
        botBlockedCount={preview?.botBlocked ?? 0}
        textUz={textUz}
        textRu={textRu}
        fileId={fileId}
        fileType={fileType}
        isSubmitting={createMutation.isPending}
        errorMessage={createMutation.error?.message}
        onConfirm={() => {
          const body: BroadcastCreateRequest = {
            audience,
            textUz: trimmedUz,
            textRu: trimmedRu,
            ...(hasMedia ? { fileId: fileId.trim(), fileType } : {}),
          }
          createMutation.mutate(body)
        }}
        onCancel={() => setPreviewOpen(false)}
      />

      <ProgressDialog broadcastId={activeBroadcastId} onClose={() => setActiveBroadcastId(null)} />
    </div>
  )
}
